<!-- src/views/Home.vue -->
<script setup lang="ts">
import { ref, onMounted, watchEffect, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useContentStore } from '@/stores/content.store'
import { useHeroStore } from '@/stores/hero.store'
import { useFrontpageStore } from '@/stores/frontpage.store'
import { useUIStore } from '@/stores/ui.store'
import { isNotFoundRoute, notFoundLocationForRoute } from '@/router/notFound'

import {
  getComponentClasses,
  normalizeBlocks,
  resolveComponentByType,
  type PageBlock,
} from '@/utils/components'

const router = useRouter()
const route = useRoute()

// Stores
const frontpageStore = useFrontpageStore()
const { frontpage, loading: fpLoading, errors: fpErrors } = storeToRefs(frontpageStore)

const contentStore = useContentStore()
const { pages, isLoading, loading, errors } = storeToRefs(contentStore)
const ui = useUIStore()

const heroStore = useHeroStore()

type PageModel = (typeof pages.value)[number]
const post = ref<PageModel | null>(null)

// Route params are almost always strings
const routeId = computed<number | undefined>(() => {
  const raw = route.params.id
  if (raw == null) return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
})

// The ID we should actually use:
// - if the route has an id -> use it
// - otherwise use frontpage.frontpage_id
const effectivePageId = computed<number | undefined>(() => {
  return routeId.value ?? frontpage.value?.frontpage_id
})

function resolveFromRoute() {
  // Wait until global init is finished
  if (isLoading.value) return
  if (loading.value.pages) return
  if (!contentStore.hasLoaded('pages')) return

  // Wait until the front page is loaded
  if (fpLoading.value?.frontpage) return
  if (ui.isBackendUnavailable) return
  if (errors.value.pages || fpErrors.value.frontpage) return
  if (!frontpage.value) return

  const id = effectivePageId.value
  if (id == null) return

  const match = pages.value.find((p) => p.id === id)

  if (!match) {
    if (!contentStore.hasFullCoverage('pages')) {
      void contentStore.ensureFullResource('pages')
      return
    }

    if (!isNotFoundRoute(route)) {
      void router.replace(notFoundLocationForRoute(route, { id: String(id) }))
    }
    return
  }

  post.value = match
  heroStore.setHeroFromSegment(match)
}

// Always make components an array (PageModel.components can be false/undefined)
const componentBlocks = computed<PageBlock[]>(() => {
  const raw = post.value?.components
  return Array.isArray(raw) ? normalizeBlocks(raw) : []
})

onMounted(async () => {
  await Promise.all([
    frontpageStore.ensure(),
    contentStore.ensureRouteResource('pages'),
  ])
})

// Reacts to: frontpage, fpLoading, isLoading, loading.pages, pages, route.params.id
watchEffect(() => {
  resolveFromRoute()
})
</script>

<template>
  <keep-alive>
    <component
      v-for="(block, index) in componentBlocks"
      :key="block.id"
      :is="resolveComponentByType(block.type, 'page')" 
      :block="block"
      :data="block"
      :data-key="index"
      :data-component="block.type"
      :data-uuid="block.raw.uuid"
      :class="getComponentClasses(block)"
    />
  </keep-alive>
</template>
